import * as React from "react";
import { Provider } from "react-redux";

import { noop } from "../../../Common/TypingUtils";
import { formDidMountEvent } from "../Engine/Core/EventManagement/ExternalEvents/ExternalEvents";
import { IForm } from "../Engine/Form/IForm";
import {
    CustomControlModelAccessor,
    CustomControlModelAccessorContext,
} from "../Forms/CustomControlsHelpers/CustomControlModelAccessor";
import { ComponentHandleRegistryProvider } from "../Engine/Core/ComponentHandleRegistry/ComponentHandleRegistryContext";
import { NidusClientProvider } from "../../../Common/NidusClient/NidusClientContext";
import { ImportEmployeesContainer } from "../StaffServiceIntegration/Employees/Components/ImportEmployeesContainer";
import { ModalManagerProvider } from "../Engine/Core/ModalManager/ModalManagerProvider";
import { getCandyHub } from "../Forms/Sentry/InitializeSentry";
import { ZeroReportOptionsProvider } from "../Engine/Core/ModalManager/Modals/ZeroReporting/Provider/ZeroReportProvoder";
import { EmployerDocumentsContainer } from "../StaffServiceIntegration/Documents/Components/EmployerDocumentsContainer";
import { ContractorsSidePageContainer } from "../ContractorsServiceIntegration/Components/ContractorsSidePageContainer";

import { UnitActionType } from "./FormParts/Unit/Store/unitActions";
import { MetricsProvider } from "./Internals/Metrics/MetricsProvider";
import "./Internals/Metrics/AutoClickMetrics";
import { ExternalEventManagerContext } from "./Services/Contexts/ExternalEventManagerContext";
import { TestPageObjectsRegistryContextProvider } from "./Services/TestPageObjects/TestPageObjectsRegistry";
import { UnitInfo } from "./UnitInfo";
import { TourElementsRegistryContextProvider } from "./Internals/TourManagement/TourElementsRegistryContext";
import { CustomErrorLogTooltipContainer } from "./FormParts/EmbeddedLog/ErrorList/ErrorLogTooltip/CustomErrorLogTooltipContainer";
import { ScrollToProvider } from "./Internals/Contexts/ScrollToContext";
import { TourStepsStorage } from "./Internals/TourManagement/TourStepsStorage";
import { TemplatesApiProvider } from "./Internals/Contexts/TemplatesApiContext";
import { UserPicklistRegistryStorage } from "./FormParts/UserPicklistDeclaration/UserPicklist/UserPicklistRegistryContext";
import { useFormObject } from "./FormObjectProvider";
import { FormDebugToolsContainer } from "./Internals/FormDebugTools/FormDebugToolsContainer";
import { FormActionHandler } from "./Internals/FormActionHandlers/FormActionHandler";
import { clearURLQueryParam } from "./FormParts/FormApplication/FormApplication";
import { ModalFormsProvider } from "./Modal/ModalForm/ModalFormsContext";
import { ExternDriveClientProvider } from "./Services/Contexts/ExternDriveClientContext";

interface BaseFormProps {
    unitInfos: UnitInfo[];
    children: JSX.Element;
    form: IForm;
    externalInitSectionAction: FormActionHandler;
    zeroReportOptions?: React.ReactNode;
    zeroReportValidationPath?: string;
    zeroReportDescription?: string;
}

export function BaseForm(props: BaseFormProps): React.ReactElement {
    const form = useFormObject();
    const customControlModelAccessor = React.useMemo(() => new CustomControlModelAccessor(form), []);
    const isProduction = form.settings.keformsUrl.includes("api.kontur.ru");

    React.useEffect(() => {
        form.store.dispatch({
            type: UnitActionType.InitUnitInfo,
            payload: { unitInfos: props.unitInfos },
        });
        form.triggerExternalEvent(formDidMountEvent());

        try {
            if (typeof props.externalInitSectionAction === "function" && form.settings.externalInitSectionPath) {
                form.publicForm.focusOnControlIfExist(form.settings.externalInitSectionPath);
                props.externalInitSectionAction(form.getHelperFunctionsCallContext(), {
                    bindingPath: form.settings.externalInitSectionPath,
                    field: "value",
                });
                clearURLQueryParam("externalInitSectionPath");
            }
        } catch (e) {
            getCandyHub().captureException(e);
        }

        return () => {
            form.hooks.beforeUnload(form.getFormHookCallContext());
        };
    }, []);

    React.useEffect(() => {
        if (form.settings.callInitZeroReport) {
            form.showCustomModal({ modalName: "ZeroReport", acceptFiles: [] });
        }
    }, []);

    return (
        <TourStepsStorage>
            <TourElementsRegistryContextProvider>
                <TemplatesApiProvider templatesApi={form.templatesApi}>
                    <TestPageObjectsRegistryContextProvider>
                        <MetricsProvider track={(action, options) => form.track(action, options)}>
                            <ScrollToProvider value={noop}>
                                <Provider store={form.store}>
                                    <ComponentHandleRegistryProvider value={form.componentHandleRegistry}>
                                        <ExternDriveClientProvider getPortalUser={() => form.getPortalUser()}>
                                            <ZeroReportOptionsProvider
                                                options={props.zeroReportOptions}
                                                validationPath={props.zeroReportValidationPath}
                                                description={props.zeroReportDescription}>
                                                <ExternalEventManagerContext.Provider value={form}>
                                                    <ModalManagerProvider>
                                                        <ModalFormsProvider form={form}>
                                                            <CustomControlModelAccessorContext.Provider
                                                                value={customControlModelAccessor}>
                                                                <NidusClientProvider client={form.nidusClient}>
                                                                    <UserPicklistRegistryStorage>
                                                                        {isProduction ? (
                                                                            props.children
                                                                        ) : (
                                                                            <FormDebugToolsContainer form={form}>
                                                                                {props.children}
                                                                            </FormDebugToolsContainer>
                                                                        )}
                                                                    </UserPicklistRegistryStorage>
                                                                </NidusClientProvider>
                                                                <CustomErrorLogTooltipContainer />
                                                                <ImportEmployeesContainer />
                                                                <ContractorsSidePageContainer />
                                                                <EmployerDocumentsContainer />
                                                            </CustomControlModelAccessorContext.Provider>
                                                        </ModalFormsProvider>
                                                    </ModalManagerProvider>
                                                </ExternalEventManagerContext.Provider>
                                            </ZeroReportOptionsProvider>
                                        </ExternDriveClientProvider>
                                    </ComponentHandleRegistryProvider>
                                </Provider>
                            </ScrollToProvider>
                        </MetricsProvider>
                    </TestPageObjectsRegistryContextProvider>
                </TemplatesApiProvider>
            </TourElementsRegistryContextProvider>
        </TourStepsStorage>
    );
}
